import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, BookOpen, Lock, RefreshCcw, Brain, ChevronRight, Target, ScrollText } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useBasicHifz } from "@/lib/hifzScope";

function GoalStat({ label, value }) {
  return (
    <div className="rounded-xl bg-sand-200/60 py-2.5">
      <div className="font-display font-semibold text-forest-700 text-lg">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

/* My Hifz — surahs, revision and a recitation-test entry point consolidated
   onto one page (nav entry replaces the old separate Surahs/Revision/Test
   sidebar destinations). */
export default function HifzPlan() {
  const { t } = useLanguage();
  const [surahs, setSurahs] = useState([]);
  const [progress, setProgress] = useState([]);
  const [queue, setQueue] = useState(null);
  const [stats, setStats] = useState(null);
  const [goalOpen, setGoalOpen] = useState(false); // UN5
  const navigate = useNavigate();
  const hs = useBasicHifz();
  // The surahs the plan has open right now (Basic Hifz, plus the ladder steps that are open): the server decides, see lib/hifzScope.js.
  const supportedSurahs = hs.allowedSurahs;

  useEffect(() => {
    Promise.all([
      api.get("/quran/surahs").then((r) => setSurahs(r.data)),
      api.get("/hifz/progress").then((r) => setProgress(r.data)),
      api.get("/revision/queue").then((r) => setQueue(r.data)).catch(() => {}),
      api.get("/hifz/stats").then((r) => setStats(r.data)).catch(() => {}),
    ]).catch(() => {});
  }, []);

  const countByStatus = (surahNum, status) =>
    progress.filter((p) => p.surah === surahNum && p.status === status).length;

  const queueItems = queue?.queue || [];
  const dueCount = queueItems.filter((q) => q.category === "recovery" || q.category === "overdue" || q.category === "recent").length;
  const longTermCount = queueItems.filter((q) => q.category === "long_term").length;

  return (
    <div className="space-y-8" data-testid="hifz-plan-page">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{t("hifzPlan.myHifz")}</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">{t("hifzPlan.yourPlanProgress")}</h1>
      </header>

      {/* UN5 — Today's goal, mirroring the home page's tile but tappable
          here for the detail behind the number. */}
      <div data-testid="hifzplan-goal">
        <button
          type="button"
          onClick={() => setGoalOpen((v) => !v)}
          className="hifz-card hifz-card-hover w-full flex items-center gap-3 text-left"
          aria-expanded={goalOpen}
          data-testid="hifzplan-goal-btn"
        >
          <div className="h-10 w-10 rounded-xl bg-gold-500/15 text-gold-700 grid place-items-center shrink-0">
            <Target className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-forest-700">{t("hifzPlan.todaySGoal")}</div>
            <div className="text-xs text-muted-foreground">
              {stats ? t("hifzPlan.ayahsDoneToday", { done: stats.daily_done ?? 0, goal: stats.daily_goal ?? 3 }) : t("hifzPlan.loading")}
            </div>
          </div>
          <ChevronRight className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${goalOpen ? "rotate-90" : ""}`} />
        </button>
        {goalOpen && stats && (
          <div className="hifz-card mt-3 space-y-3" data-testid="hifzplan-goal-detail">
            <div>
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                <span>{t("hifzPlan.today")}</span>
                <span>{stats.daily_done ?? 0}/{stats.daily_goal ?? 3}</span>
              </div>
              <div className="h-2 rounded-full bg-sand-200 overflow-hidden">
                <div
                  className="h-full bg-gold-500 transition-all"
                  style={{ width: `${Math.min(100, Math.round(((stats.daily_done ?? 0) / (stats.daily_goal || 3)) * 100))}%` }}
                />
              </div>
            </div>
            {/* M1 — trimmed to just revision + memorization info (streak
                already lives near the bell icon; "learning" dropped as
                noise) and "Continue your revision" merged in here instead
                of its own standalone card below. */}
            <div className="grid grid-cols-2 gap-3 text-center">
              <GoalStat label={t("hifzPlan.memorized")} value={stats.total_memorized ?? 0} />
              <GoalStat label={t("hifzPlan.revisionDue")} value={dueCount} />
            </div>
            <p className="text-xs text-muted-foreground">
              {(stats.daily_done ?? 0) >= (stats.daily_goal ?? 3)
                ? t("hifzPlan.todaySGoalIsComplete")
                : t("hifzPlan.moreAyahsToGoal", { count: (stats.daily_goal ?? 3) - (stats.daily_done ?? 0) })}
            </p>
            <Link
              to="/app/revision"
              className="flex items-center gap-3 rounded-xl bg-terracotta-500/10 hover:bg-terracotta-500/15 transition px-3 py-2.5 -mx-1"
              data-testid="hifzplan-revision-summary"
            >
              <div className="h-9 w-9 rounded-lg bg-terracotta-500/15 text-terracotta-600 grid place-items-center shrink-0">
                <RefreshCcw className="h-4.5 w-4.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-forest-700">{t("hifzPlan.continueYourRevision")}</div>
                <div className="text-xs text-muted-foreground">
                  {dueCount > 0 ? t("hifzPlan.reviewsDue", { count: dueCount }) : t("hifzPlan.allCaughtUp")} · {t("hifzPlan.longTermCount", { count: longTermCount })}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </Link>
          </div>
        )}
      </div>

      {/* Recitation test entry point — AI feedback, quick practice, or self
          test. (Was briefly removed under H1, then restored on request.) */}
      <Link to="/app/hifz-test" className="hifz-card hifz-card-hover flex items-center gap-3" data-testid="hifzplan-test-cta">
        <div className="h-10 w-10 rounded-xl bg-forest-600/12 text-forest-700 grid place-items-center shrink-0">
          <Brain className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-forest-700">{t("hifzPlan.startAHifzTest")}</div>
          <div className="text-xs text-muted-foreground">{t("hifzPlan.aiFeedbackQuickPracticeOr")}</div>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </Link>

      {/* Basic Hifz: free for everyone, with the reason each piece is there */}
      <Link to="/app/basic-hifz" className="hifz-card hifz-card-hover flex items-center gap-3" data-testid="hifzplan-basic-cta">
        <div className="h-10 w-10 rounded-xl bg-gold-500/15 text-gold-700 grid place-items-center shrink-0">
          <ScrollText className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-forest-700">{t("hifzPlan.basicHifzCta")}</div>
          <div className="text-xs text-muted-foreground">
            {hs.data ? t("hifzPlan.basicHifzCtaSub", { done: hs.data.memorized, total: hs.data.total }) : t("hifzPlan.loading")}
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </Link>

      <div className="space-y-3" data-testid="hifz-surah-list">
        {!hs.loading && surahs.map((s) => {
          const supported = supportedSurahs.includes(s.number);
          const memorized = countByStatus(s.number, "memorized");
          const learning = countByStatus(s.number, "learning");
          const total = hs.scopeCount(s.number) || s.available_ayahs || s.total_ayahs;
          const pct = Math.round((memorized / total) * 100);
          if (!supported) {
            return (
              <div
                key={s.number}
                role="button"
                tabIndex={-1}
                onClick={() => { toast.info(t("hifzPlan.notOpenYet")); navigate("/app/basic-hifz"); }}
                className="hifz-card flex items-center justify-between gap-4 opacity-60 select-none cursor-pointer"
                aria-disabled="true"
                data-testid={`hifz-surah-locked-${s.number}`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="ayah-arabic text-2xl text-foreground/50 shrink-0">{s.name_ar}</div>
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">{t("hifzPlan.surah", { number: s.number, revelation: s.revelation })}</div>
                    <div className="font-display font-semibold text-lg text-forest-700/70 truncate">{s.name_en}</div>
                  </div>
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sand-200 text-muted-foreground text-xs font-medium shrink-0" data-testid={`coming-soon-${s.number}`}>
                  <Lock className="h-3 w-3" /> {t("hifzPlan.lockedPremium")}
                </div>
              </div>
            );
          }
          return (
            <Link key={s.number} to={`/app/hifz/${s.number}`} className="hifz-card hifz-card-hover flex items-center justify-between gap-4" data-testid={`hifz-surah-${s.number}`}>
              <div className="flex items-center gap-4 min-w-0">
                <div className="ayah-arabic text-2xl text-foreground/85 shrink-0">{s.name_ar}</div>
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">{t("hifzPlan.surah", { number: s.number, revelation: s.revelation })}</div>
                  <div className="font-display font-semibold text-lg text-forest-700 truncate">{s.name_en}</div>
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <span className="status-pill status-memorized">{t("hifzPlan.memorized2", { memorized })}</span>
                    {learning > 0 && <span className="status-pill status-learning">{t("hifzPlan.learning", { learning })}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="w-28">
                  <div className="text-right text-xs text-muted-foreground">{pct}%</div>
                  <div className="mt-1 h-2 bg-sand-200 rounded-full overflow-hidden">
                    <div className="h-full bg-forest-600" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-forest-700" />
              </div>
            </Link>
          );
        })}
      </div>

      <div className="hifz-card flex items-start gap-4 bg-sand-200/60 border-sand-300">
        <BookOpen className="h-5 w-5 text-gold-600 mt-1" />
        <div className="text-sm text-foreground/80">
          {t("hifzPlan.freeScopeNote")}{" "}
          <Link to="/app/basic-hifz" className="font-semibold text-forest-700 underline" data-testid="hifzplan-see-steps">{t("basicHifz.seeSteps")}</Link>
        </div>
      </div>
    </div>
  );
}
